import React, { useState, useEffect } from 'react';
import InfoTooltip from './InfoTooltip';

/**
 * KPI Card Component with Tooltip
 */
const KpiCard = ({ title, value, unit, subtitle, detail, tooltip }) => {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow h-full flex flex-col justify-between">
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-xs font-medium text-gray-600 flex-1">{title}</h4>
        {tooltip && <InfoTooltip content={tooltip} />}
      </div>
      
      <div>
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-2xl font-bold text-[#570683]">{value}</span>
          {unit && <span className="text-xs font-semibold text-gray-500">{unit}</span>}
        </div>
        
        {subtitle && (
          <div className="text-xs text-gray-500">{subtitle}</div>
        )}
        
        {detail && (
          <div className="text-xs text-[#417E46] mt-1 font-medium">{detail}</div>
        )}
      </div>
    </div>
  );
};

/**
 * Left Panel Component - Brand & Live Energy
 */
const LoginLeft = () => {
  const [liveData, setLiveData] = useState(null);

  useEffect(() => {
    const fetchLiveData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/live-energy/kpis`);
        const data = await response.json();
        
        if (data.success) {
          setLiveData(data);
        }
      } catch (error) {
        console.error('Error fetching live data:', error);
      }
    };

    fetchLiveData();
    const interval = setInterval(fetchLiveData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="h-full w-full flex flex-col relative p-8">
      {/* Logo Header */}
      <header className="mb-6">
        <img 
          src="/assets/urgaa-logo-final.jpg" 
          alt="Go4Garage URGAA"
          className="w-full max-w-md h-auto object-contain"
        />
      </header>

      {/* Tagline - Bold Orange */}
      <div className="mb-6">
        <h1 
          className="text-2xl font-bold tracking-tight mb-2"
          style={{ color: '#DF8C4D', fontFamily: 'Inter', fontWeight: 700 }}
        >
          URGAA – India's First EV Infrastructure EcoSystem
        </h1>
        <p className="text-sm text-gray-600 flex items-center gap-1">
          <span className="font-bold">Charging Tomorrow with Green Energy</span> • <span className="font-bold">Made in Bharat</span> <span className="text-base">🇮🇳</span>
        </p>
      </div>

      {/* Section Title */}
      <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
        LIVE ENERGY & OUTLOOK
      </h2>

      {/* 4 KPI Cards in 2×2 Grid */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <KpiCard
          title="India's Demand"
          value={liveData?.kpis.india_road_transport_demand.value || "49.44M"}
          subtitle="kWh today"
          detail="2W/3W/4W/Trucks"
          tooltip="Estimated national EV energy demand for road transport. Aggregates 2W/3W/4W/Trucks and updates periodically."
        />
        
        <KpiCard
          title="G4G Supply"
          value={liveData?.kpis.g4g_supplied.value + "K" || "45.0K"}
          subtitle="kWh today"
          detail="↗ +18.6% vs 7d"
          tooltip="Energy delivered across URGAA chargers in the selected window."
        />
        
        <KpiCard
          title="Market Share"
          value={liveData?.kpis.market_share.value + "%" || "0.091%"}
          detail="G4G share • Growing daily"
          tooltip="Share of public charging sessions attributed to Go4Garage in your region."
        />
        
        <KpiCard
          title="5-Year Growth"
          value="62 → 595"
          unit="GWh"
          subtitle="(2025-2030)"
          detail="+860% CAGR"
          tooltip="Projected energy delivered under base/optimistic scenarios based on current pipeline."
        />
      </div>

      {/* Mascot - Bottom Right with Glow */}
      <div className="absolute bottom-8 right-8 pointer-events-none hidden md:block">
        <div className="relative">
          <img 
            src="/assets/mascot-anim.gif"
            alt="URGAA mascot"
            className="w-48 h-auto object-contain"
          />
          {/* Soft Green Glow */}
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-4 rounded-full blur-lg opacity-50"
            style={{ background: 'radial-gradient(50% 50% at 50% 50%, rgba(13,163,78,0.6) 0%, rgba(13,163,78,0) 100%)' }}
          />
        </div>
      </div>
    </section>
  );
};

export default LoginLeft;
